/**
 * Acquisition tracking schema (Phase 1 of the ops data plan).
 *
 * Source of truth for everything that joins a paying member back to the
 * spend that acquired them. See:
 *   - docs/markdown/eng/CTO_BRIEF_ACQUISITION_TRACKING.md (full spec)
 *   - marketing/MELIO_CHANNEL_TEST_PLAN_V1.md (parent plan)
 *
 * THREE COLLECTIONS / FIELDS:
 *   1. campaigns/{campaignId}              — one doc per marketing placement
 *   2. users/{uid}.acquisition             — written once at signup, immutable
 *   3. acquisition_summary/{yyyy_mm}       — nightly materialised aggregate
 *
 * IMPORTANT (PHI / privacy):
 *   - The acquisition block on a user doc is NOT clinical data. It contains
 *     channel, campaign id, first-touch path, UTMs, promo code, optional
 *     referrer uid. No biomarkers, no symptoms, no IP address.
 *   - It IS personal data under GDPR. Include it in export / erasure flows.
 */

import type { Timestamp } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// CHANNEL ENUM
// ---------------------------------------------------------------------------

export type AcquisitionChannel =
  | 'podcast'
  | 'content'       // Substack / SEO / YouTube / longform
  | 'referral'
  | 'partnership'
  | 'pr'
  | 'paid'          // reserved; not used in 6-month plan
  | 'direct'        // no identifiable source (homepage, bookmark, typed url)
  | 'other';

export const ACQUISITION_CHANNELS: AcquisitionChannel[] = [
  'podcast',
  'content',
  'referral',
  'partnership',
  'pr',
  'paid',
  'direct',
  'other',
];

export const ACQUISITION_CHANNEL_LABELS: Record<AcquisitionChannel, string> = {
  podcast: 'Podcast',
  content: 'Content / SEO',
  referral: 'Referral',
  partnership: 'Partnership',
  pr: 'PR',
  paid: 'Paid',
  direct: 'Direct',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// 1. CAMPAIGNS
// ---------------------------------------------------------------------------

/**
 * One document per marketing placement. Created BEFORE the campaign goes
 * live by an admin (Jonathan) or via a seed script. A missing campaignId
 * blocks the landing page from rendering.
 *
 * `campaignId` naming convention: `{channel}_{slug}_{yyyy_mm}`.
 *   podcast_chatterjee_2026_07
 *   content_learn_apob_evergreen
 *   pr_ft_weekend_2026_06
 *   partnership_thirdspace_2026_09
 *   referral_default              (single doc, all referrals share unless we
 *                                  run a referral-specific campaign)
 */
export interface CampaignDoc {
  campaignId: string;
  channel: AcquisitionChannel;
  slug: string;
  displayName: string;
  hostOrPartnerName?: string;
  promoCode?: string | null;
  airDate?: Timestamp | null;
  plannedSpendGbp: number;
  actualSpendGbp?: number | null;
  landingHeroCopy: string;
  landingCtaLabel: string;
  active: boolean;
  createdAt: Timestamp;
  createdBy: string;
  notes?: string | null;
}

// ---------------------------------------------------------------------------
// 2. FIRST-TOUCH COOKIE PAYLOAD (client-side, transient)
// ---------------------------------------------------------------------------

/**
 * Shape of the JSON we serialise into the `melio_firsttouch` cookie at
 * first touch and read back at signup time. Not persisted in Firestore;
 * the Cloud Function transforms this into the acquisition block on the
 * user doc.
 */
export interface FirstTouchPayload {
  channel: AcquisitionChannel;
  campaignId: string | null;
  firstTouchAt: string;              // ISO 8601, set by the client
  firstTouchPath: string;            // e.g. "/c/rangan", "/learn/apob"
  firstTouchReferrer: string | null; // hostname only; never full url with query
  firstTouchUtm: FirstTouchUtm;
  promoCode: string | null;
  referrerUid: string | null;        // set only by the /invite/:code path
  schemaVersion: 1;
}

export interface FirstTouchUtm {
  source?: string;                   // utm_source
  medium?: string;                   // utm_medium
  campaign?: string;                 // utm_campaign
  content?: string;                  // utm_content
  term?: string;                     // utm_term
}

// ---------------------------------------------------------------------------
// 3. USER ACQUISITION BLOCK (server-written, immutable)
// ---------------------------------------------------------------------------

/**
 * Lives on `users/{uid}.acquisition`. Written ONCE by `completeSignup`
 * (Cloud Function) and never mutated client-side. Firestore rules block
 * the client from updating any key under `acquisition`.
 *
 * The Stripe webhook MAY patch `promoCodeAtSignup` post-hoc if a coupon
 * was applied at checkout and the cookie was missing — see Phase 2.
 */
export interface UserAcquisition {
  channel: AcquisitionChannel;
  campaignId: string | null;
  firstTouchAt: Timestamp;
  firstTouchPath: string;
  firstTouchReferrer: string | null;
  firstTouchUtm: FirstTouchUtm;
  promoCodeAtSignup: string | null;
  referrerUid: string | null;
  daysFromFirstTouchToSignup: number;
  schemaVersion: 1;
}

// ---------------------------------------------------------------------------
// 4. NIGHTLY AGGREGATE
// ---------------------------------------------------------------------------

/**
 * `acquisition_summary/{yyyy_mm}` — materialised by the nightly Cloud
 * Function. Read by the ops portal acquisition dashboard and (later) by
 * investor-facing CAC tables.
 *
 * `cpl` and `cac` are NULL (not zero, not infinity) when the denominator
 * is zero. The UI distinguishes "no data yet" from "£0".
 */
export interface AcquisitionSummaryDoc {
  period: string;                    // "2026_07"
  generatedAt: Timestamp;
  totals: AcquisitionTotals;
  byCampaign: Record<string, AcquisitionCampaignRollup>;
  byChannel: Partial<Record<AcquisitionChannel, AcquisitionChannelRollup>>;
}

export interface AcquisitionTotals {
  waitlistSignups: number;
  paidMembers: number;
  totalSpendGbp: number;
}

export interface AcquisitionCampaignRollup {
  channel: AcquisitionChannel;
  waitlistSignups: number;
  paidMembers: number;
  spendGbp: number;
  cpl: number | null;
  cac: number | null;
  firstTouchAt: Timestamp | null;
  lastSignupAt: Timestamp | null;
}

export interface AcquisitionChannelRollup {
  waitlistSignups: number;
  paidMembers: number;
  spendGbp: number;
  cpl: number | null;
  cac: number | null;
}
